import React, { useState } from 'react';

const WorksheetBatchModal = ({
  isOpen,
  onClose,
  selectedTestIds = [],
  selectedItems = [],
  analyzers = [],
  onCreateBatch,
  onPrintBatch
}) => {
  const [batchName, setBatchName] = useState(() => {
    const now = new Date();
    const timeStr = now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }).replace(':', '');
    return `CBC-Batch-${now.toISOString().split('T')[0]}-${timeStr}`;
  });
  const [selectedAnalyzerId, setSelectedAnalyzerId] = useState('');
  const [remarks, setRemarks] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (!isOpen) return null;

  const handleCreate = async (autoPrint = false) => {
    if (!batchName.trim()) {
      alert('Please enter a batch name.');
      return;
    }
    if (selectedTestIds.length === 0) {
      alert('No specimens selected for this batch.');
      return;
    }

    setIsSubmitting(true);
    try {
      const payload = {
        batchName: batchName.trim(),
        subCategoryId: selectedItems[0]?.subCategoryId || null,
        analyzerId: selectedAnalyzerId ? parseInt(selectedAnalyzerId, 10) : null,
        testIds: selectedTestIds,
        remarks: remarks.trim()
      };

      const result = await onCreateBatch(payload);
      if (autoPrint && result?.batchId) {
        onPrintBatch(result.batchId, selectedTestIds);
      }
      onClose();
    } catch (err) {
      console.error('Batch creation failed:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="ws-modal-overlay" onClick={onClose}>
      <div
        className="ws-modal-card"
        style={{ width: '640px' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="ws-modal-header">
          <div>
            <h3 style={{ margin: 0, fontSize: '17px', color: '#0f172a' }}>
              🔬 Create Diagnostic Batch Run
            </h3>
            <div style={{ fontSize: '12px', color: '#64748b', marginTop: '2px' }}>
              Assign <strong>{selectedTestIds.length}</strong> selected specimens to an analyzer run
            </div>
          </div>
          <button
            type="button"
            className="ws-btn ws-btn-outline ws-btn-sm"
            onClick={onClose}
          >
            ✕
          </button>
        </div>

        <div className="ws-modal-body" style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
          {/* Batch Name */}
          <div className="ws-filter-group">
            <label className="ws-filter-label">Batch Name / Identifier *</label>
            <input
              type="text"
              className="ws-filter-input"
              value={batchName}
              onChange={(e) => setBatchName(e.target.value)}
              placeholder="e.g. Morning CBC Sysmex Run 1"
            />
          </div>

          {/* Assigned Analyzer */}
          <div className="ws-filter-group">
            <label className="ws-filter-label">Target Laboratory Analyzer / Instrument</label>
            <select
              className="ws-filter-select"
              value={selectedAnalyzerId}
              onChange={(e) => setSelectedAnalyzerId(e.target.value)}
            >
              <option value="">-- Manual Testing / Benchtop --</option>
              {analyzers.map((a) => (
                <option key={a.analyzerId} value={a.analyzerId}>
                  {a.analyzerName} ({a.model}) - {a.sectionName || 'Pathology'}
                </option>
              ))}
            </select>
          </div>

          {/* Remarks */}
          <div className="ws-filter-group">
            <label className="ws-filter-label">Batch Notes / Bench Remarks</label>
            <input
              type="text"
              className="ws-filter-input"
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
              placeholder="e.g. Calibrated at 08:30 AM, reagents lot #4892"
            />
          </div>

          {/* Selected Specimens Summary Table */}
          <div>
            <label className="ws-filter-label" style={{ display: 'block', marginBottom: '6px' }}>
              Included Specimens Preview ({selectedItems.length})
            </label>
            <div style={{ maxHeight: '180px', overflowY: 'auto', border: '1px solid #e2e8f0', borderRadius: '6px' }}>
              <table className="ws-table" style={{ fontSize: '12px' }}>
                <thead>
                  <tr>
                    <th>Sample No</th>
                    <th>Lab No</th>
                    <th>Patient Name</th>
                    <th>Test</th>
                    <th>Priority</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedItems.map((item) => (
                    <tr key={item.testId}>
                      <td style={{ fontWeight: 700 }}>{item.sampleNo}</td>
                      <td>{item.labNo}</td>
                      <td>{item.patientName}</td>
                      <td>{item.testName}</td>
                      <td>
                        <span className={`ws-badge ${item.priority === 'STAT' ? 'badge-stat' : 'badge-routine'}`}>
                          {item.priority}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <div className="ws-modal-footer">
          <button
            type="button"
            className="ws-btn ws-btn-outline"
            onClick={onClose}
            disabled={isSubmitting}
          >
            Cancel
          </button>
          <button
            type="button"
            className="ws-btn ws-btn-outline"
            onClick={() => handleCreate(true)}
            disabled={isSubmitting}
          >
            🖨️ Create &amp; Print Batch
          </button>
          <button
            type="button"
            className="ws-btn ws-btn-primary"
            onClick={() => handleCreate(false)}
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Creating Batch...' : 'Start Batch Run'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default WorksheetBatchModal;
